import { useEffect, useMemo } from 'react';

/**
 * SEO head injector — manipulates document.head directly via useEffect so it
 * works reliably under React 18 concurrent rendering (react-helmet v6 has
 * known issues there). Renders nothing.
 *
 * On unmount/route-change we restore the original meta values from
 * index.html so the homepage / non-dealer pages keep their global SEO.
 */
const META_KEYS = ['description', 'keywords'];
const OG_KEYS = ['og:type', 'og:title', 'og:description', 'og:url', 'og:image'];
const TWITTER_KEYS = ['twitter:card', 'twitter:title', 'twitter:description', 'twitter:image'];
// Favicon <link rel="..."> variants that dealer microsites should override.
const FAVICON_RELS = ['icon', 'shortcut icon', 'apple-touch-icon'];

function setMeta(name, value, byProperty = false) {
  if (!value) return;
  const attr = byProperty ? 'property' : 'name';
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setCanonical(url) {
  if (!url) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', url);
}

// Marker so we know which favicon links we own (to remove them on unmount).
const FAV_FLAG = 'data-seo-favicon';

function clearOwnedFavicons() {
  document.head.querySelectorAll(`link[${FAV_FLAG}="1"]`).forEach((n) => n.remove());
}

function applyFavicon(href) {
  if (!href) return;
  // First, hide the default favicons so the browser doesn't fall back to them.
  document.head.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]').forEach((n) => {
    if (n.getAttribute(FAV_FLAG) !== '1') n.setAttribute('data-seo-original-fav', '1');
    n.remove();
  });
  // Then inject dealer-owned ones for each rel variant so browser + iOS + Android all pick it up.
  FAVICON_RELS.forEach((rel) => {
    const link = document.createElement('link');
    link.setAttribute('rel', rel);
    link.setAttribute('href', href);
    link.setAttribute(FAV_FLAG, '1');
    document.head.appendChild(link);
  });
}

const JSONLD_FLAG = 'data-seo-jsonld';

function clearJsonLd() {
  document.head
    .querySelectorAll(`script[type="application/ld+json"][${JSONLD_FLAG}="1"]`)
    .forEach((n) => n.remove());
}

function pushJsonLd(obj) {
  if (!obj) return;
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.setAttribute(JSONLD_FLAG, '1');
  s.textContent = JSON.stringify(obj);
  document.head.appendChild(s);
}

const SEO = ({ title, description, canonical, image, favicon, type = 'website', jsonLd }) => {
  const ldArray = useMemo(() => {
    if (!jsonLd) return [];
    return Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  }, [jsonLd]);

  useEffect(() => {
    // Snapshot originals so non-dealer pages restore cleanly on unmount.
    const original = {
      title: document.title,
      meta: {},
      og: {},
      twitter: {},
      canonical: document.head.querySelector('link[rel="canonical"]')?.href || '',
      favicons: Array.from(
        document.head.querySelectorAll('link[rel~="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"]')
      ).map((n) => ({ rel: n.getAttribute('rel'), href: n.getAttribute('href'), sizes: n.getAttribute('sizes'), type: n.getAttribute('type') })),
    };
    META_KEYS.forEach((k) => {
      original.meta[k] = document.head.querySelector(`meta[name="${k}"]`)?.content || '';
    });
    OG_KEYS.forEach((k) => {
      original.og[k] = document.head.querySelector(`meta[property="${k}"]`)?.content || '';
    });
    TWITTER_KEYS.forEach((k) => {
      original.twitter[k] = document.head.querySelector(`meta[name="${k}"]`)?.content || '';
    });

    const trimmedTitle = (title || document.title || 'Propitstop').slice(0, 70);
    const trimmedDesc = (description || '').slice(0, 200);

    document.title = trimmedTitle;
    if (trimmedDesc) setMeta('description', trimmedDesc);

    setMeta('og:type', type, true);
    setMeta('og:title', trimmedTitle, true);
    if (trimmedDesc) setMeta('og:description', trimmedDesc, true);
    if (canonical) setMeta('og:url', canonical, true);
    if (image) setMeta('og:image', image, true);

    setMeta('twitter:card', image ? 'summary_large_image' : 'summary');
    setMeta('twitter:title', trimmedTitle);
    if (trimmedDesc) setMeta('twitter:description', trimmedDesc);
    if (image) setMeta('twitter:image', image);

    setCanonical(canonical);

    if (favicon) applyFavicon(favicon);

    clearJsonLd();
    ldArray.forEach(pushJsonLd);

    return () => {
      // Restore originals so homepage keeps its global meta
      document.title = original.title;
      META_KEYS.forEach((k) => {
        if (original.meta[k]) setMeta(k, original.meta[k]);
      });
      OG_KEYS.forEach((k) => {
        if (original.og[k]) setMeta(k, original.og[k], true);
      });
      TWITTER_KEYS.forEach((k) => {
        if (original.twitter[k]) setMeta(k, original.twitter[k]);
      });
      if (original.canonical) setCanonical(original.canonical);
      // Remove any favicons we injected and restore defaults.
      if (favicon) {
        clearOwnedFavicons();
        original.favicons.forEach((f) => {
          const l = document.createElement('link');
          l.setAttribute('rel', f.rel);
          if (f.href) l.setAttribute('href', f.href);
          if (f.sizes) l.setAttribute('sizes', f.sizes);
          if (f.type) l.setAttribute('type', f.type);
          document.head.appendChild(l);
        });
      }
      clearJsonLd();
    };
  }, [title, description, canonical, image, favicon, type, ldArray]);

  return null;
};

export default SEO;
